import { useEffect, useMemo, useState } from "react";
import { EnrollmentRestriction, EnrollmentRestrictionOperator } from "@models/ISchema";
import { Option } from "react-select/lib/filters";

export type RestrictionOption = Option & {
  self?: any;
};

interface useGroupRestrictionsValidationsProps {
  inheritedSubjectRestrictionsByKey: {
    [key: string]: {
      label: string;
      value: string;
      operatorLabel: string;
      self: EnrollmentRestriction;
    };
  };
  groupRestrictionsByKey: {
    [key: string]: {
      label: string;
      ogOperatorLabel?: EnrollmentRestrictionOperator;
      operatorLabel: EnrollmentRestrictionOperator;
      self: EnrollmentRestriction;
      value: string;
    };
  };
}

interface FilteredSelectedOperator {
  label: string;
  value: string;
  isInherited: boolean;
}

const useGroupRestrictionsValidations = ({
  inheritedSubjectRestrictionsByKey,
  groupRestrictionsByKey,
}: useGroupRestrictionsValidationsProps) => {
  const [selectedProperty, setSelectedProperty] = useState<RestrictionOption | null>(null);
  const [selectedOperator, setSelectedOperator] = useState<FilteredSelectedOperator | null>(null);

  const filteredSelectedOperator = useMemo(() => {
    if (selectedProperty && selectedProperty?.value in inheritedSubjectRestrictionsByKey) {
      const inheritedValue = inheritedSubjectRestrictionsByKey[selectedProperty?.value];

      return {
        label: inheritedValue?.operatorLabel,
        value: inheritedValue?.self?.operator,
        isInherited: true,
      };
    }

    return selectedOperator;
  }, [selectedProperty, inheritedSubjectRestrictionsByKey, selectedOperator]);

  useEffect(() => {
    if (
      selectedProperty &&
      !(selectedProperty?.value in inheritedSubjectRestrictionsByKey) &&
      selectedProperty?.value in groupRestrictionsByKey
    ) {
      const groupValue = groupRestrictionsByKey[selectedProperty?.value];

      setSelectedOperator({
        label: groupValue?.operatorLabel,
        value: groupValue?.self?.operator,
        isInherited: false,
      });
    }
  }, [selectedProperty]);

  const handleSetSelectedProperty = (property: RestrictionOption | null) => {
    setSelectedOperator(null);
    setSelectedProperty(property);
  };

  return {
    selectedProperty,
    selectedOperator: filteredSelectedOperator,
    setSelectedProperty: handleSetSelectedProperty,
    setSelectedOperator,
  };
};

export default useGroupRestrictionsValidations;
